/* ═══════════════════════════════════════════════════════════════════════
   React Query hooks — data fetching with automatic caching & dedup
   ═══════════════════════════════════════════════════════════════════════ */

import { useQuery, useQueries } from "@tanstack/react-query";
import {
  fetchCompanies,
  fetchSummary,
  fetchTrend,
  fetchCompare,
  searchTickers,
  fetchQuote,
  fetchConceptCatalog,
  fetchPeers,
  fetchPriceHistory,
  fetchFilings,
  fetchInsiders,
  fetchInsiderTrades,
  fetchInstitutionalHolders,
} from "../api/client";

// ── Companies (fetched once, cached forever) ─────────────────────────

export function useCompanies() {
  return useQuery({
    queryKey: ["companies"],
    queryFn: fetchCompanies,
    staleTime: Infinity,
  });
}

// ── Insiders & Holders ───────────────────────────────────────────────

export function useInsiders(ticker: string | undefined) {
  return useQuery({
    queryKey: ["insiders", ticker],
    queryFn: () => fetchInsiders(ticker!),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInsiderTrades(ticker: string | undefined) {
  return useQuery({
    queryKey: ["insiderTrades", ticker],
    queryFn: () => fetchInsiderTrades(ticker!),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInstitutionalHolders(ticker: string | undefined) {
  return useQuery({
    queryKey: ["institutionalHolders", ticker],
    queryFn: () => fetchInstitutionalHolders(ticker!),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFilings(ticker: string | undefined) {
  return useQuery({
    queryKey: ["filings", ticker],
    queryFn: () => fetchFilings(ticker!),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

// ── Summary per ticker ───────────────────────────────────────────────

export function useSummary(exchange: string | undefined, ticker: string | undefined) {
  return useQuery({
    queryKey: ["summary", exchange, ticker],
    queryFn: () => fetchSummary(exchange!, ticker!),
    enabled: !!ticker && !!exchange,
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

// ── Trend (single) ──────────────────────────────────────────────────

export function useTrend(
  exchange: string | undefined,
  ticker: string | undefined,
  concept: string,
  periods = 8,
  form?: string,
) {
  return useQuery({
    queryKey: ["trend", exchange, ticker, concept, periods, form],
    queryFn: () => fetchTrend(exchange!, ticker!, concept, periods, form),
    enabled: !!ticker && !!exchange,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Trend (batch — for charts builder with multiple entity×metric) ──

export function useTrendBatch(
  queries: Array<{
    exchange: string;
    ticker: string;
    concept: string;
    periods: number;
    form?: string;
  }>,
) {
  return useQueries({
    queries: queries.map((q) => ({
      queryKey: ["trend", q.exchange, q.ticker, q.concept, q.periods, q.form],
      queryFn: () => fetchTrend(q.exchange, q.ticker, q.concept, q.periods, q.form),
      staleTime: 5 * 60 * 1000,
    })),
  });
}

// ── Compare ──────────────────────────────────────────────────────────

export function useCompare(
  exchange: string | undefined,
  concept: string,
  tickers: string[],
  periods = 1,
  form?: string,
) {
  return useQuery({
    queryKey: ["compare", exchange, concept, tickers, periods, form],
    queryFn: () => fetchCompare(exchange!, concept, tickers, periods, form),
    enabled: tickers.length > 0 && !!exchange,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Ticker Universe search (debounced) ───────────────────────────────

export function useTickerSearch(query: string) {
  return useQuery({
    queryKey: ["tickerSearch", query],
    queryFn: () => searchTickers(query, 10),
    enabled: query.trim().length >= 1,
    staleTime: 30 * 1000, // 30s
    placeholderData: (prev) => prev,
  });
}

// ── Quote ────────────────────────────────────────────────────────────

export function useQuote(ticker: string | undefined) {
  return useQuery({
    queryKey: ["quote", ticker],
    queryFn: () => fetchQuote(ticker!),
    enabled: !!ticker,
    staleTime: 60 * 1000, // 1 min
    retry: 1,
  });
}

// ── Concept Catalog (per company — for dynamic metric discovery) ─────

export function useConceptCatalog(exchange: string | undefined, ticker: string | undefined) {
  return useQuery({
    queryKey: ["conceptCatalog", exchange, ticker],
    queryFn: () => fetchConceptCatalog(exchange!, ticker!),
    enabled: !!ticker && !!exchange,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Peers (SIC-matched) ──────────────────────────────────────────────

export function usePeers(ticker: string | undefined) {
  return useQuery({
    queryKey: ["peers", ticker],
    queryFn: () => fetchPeers(ticker!),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Price History ────────────────────────────────────────────────────

export function usePriceHistory(ticker: string | undefined, period = "1y") {
  return useQuery({
    queryKey: ["priceHistory", ticker, period],
    queryFn: () => fetchPriceHistory(ticker!, period),
    enabled: !!ticker,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
